/**
* FGA Add Group Panel Component
*
* Displays a drawer-based panel for creating a new group.
* Allows entering a group name with client-side validation.
*
* This component is responsible for:
* - Handling group name input
* - Performing client-side validation (format and duplicate check)
* - Displaying validation or API errors
* - Emitting events when a group should be created or the panel closed
*/

import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, ElementRef, EventEmitter, Input, OnChanges, Output, signal, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FgaIconComponent } from '../fga-icon/fga-icon.component';
import { DrawerModule } from 'primeng/drawer';
import { ButtonModule } from 'primeng/button'; 
import { InputTextModule } from 'primeng/inputtext';
import { validateGroupName, isDuplicateGroupName, GROUP_NAME_ERRORS, GROUP_NAME_MAX_LENGTH } from '../../utils/validation';

/**
* Features:
* - Drawer-based UI using AngularKit Drawer
* - Group name validation (length, format, duplicates)
* - Reactive validation state using Angular signals
* - Displays validation and API errors
* - Emits add and close events to parent
* - OnPush change detection for performance
*
* @example
* <fga-add-group-panel
    [open]="addGroupOpen"
    [groupNames]="groupNames"
    [addingGroup]="addingGroup"
    [addGroupError]="addGroupError"
    (addGroup)="onAddGroup($event)"
    (closePanel)="onCloseAddGroup()">
* ></fga-add-group-panel>
*/

@Component({
  selector: 'fga-add-group-panel',
  imports: [CommonModule, FormsModule, DrawerModule, ButtonModule, InputTextModule, FgaIconComponent],
  templateUrl: './fga-add-group-panel.component.html',
  styleUrls: ['./fga-add-group-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FgaAddGroupComponent implements OnChanges {
  // ===========================================================================
  // Inputs
  // ===========================================================================

  /** Controls whether the drawer is visible. */
  @Input()
  open = false;

  /** Signal function indicating if a group is being added. */
  @Input()
  addingGroup: () => boolean = () => false;

  /** Signal function exposing add group API error. */
  @Input()
  addGroupError: () => string | null = () => null;

  /** Signal function providing existing group names for duplicate check. */
  @Input()
  groupNames: () => string[] = () => [];

  // ===========================================================================
  // Outputs
  // ===========================================================================

  /** Emits when the panel should be closed. */
  @Output()
  closePanel = new EventEmitter<void>();

  /** Emits when selection state changes. */
  @Output()
  addGroup = new EventEmitter<string>();

  /** Emitted when user modifies the add group input (to clear API errors). */
  @Output()
  addGroupInputChange = new EventEmitter<void>();

  // ===========================================================================
  // Local state
  // ===========================================================================

  /** Reference to the input element for direct DOM manipulation */
  @ViewChild('newGroupInput') private inputRef?: ElementRef<HTMLInputElement>;

  /** Current value of the new group input */
  readonly newGroupName = signal('');

  /** Whether user has interacted with the input */
  readonly hasInteracted = signal(false);

  /** Expose max length constant to template for input binding */
  readonly groupNameMaxLength = GROUP_NAME_MAX_LENGTH;

  /** Combined error from live validation or API */
  readonly displayError = computed(() => {
    if (!this.hasInteracted()) {
      return this.addGroupError();
    }
    return this.localValidationResult().error || this.addGroupError();
  });

  /** Whether input has an error (for styling) */
  readonly hasError = computed(() => !!this.displayError());

  /** Whether Save button should be disabled */
  readonly isSaveButtonDisabled = computed(() => {
    return !this.localValidationResult().valid || this.addingGroup();
  });

  /** Combined client-side validation result for the group name (format + duplicate) */
  readonly localValidationResult = computed(() => {
    const name = this.newGroupName().trim();
    if (!name) {
      return { valid: false, error: null };
    }
  
    const validation = validateGroupName(name);
    if (!validation.valid) {
      return validation;
    }
  
    if (isDuplicateGroupName(name, this.groupNames())) {
      return { valid: false, error: GROUP_NAME_ERRORS.DUPLICATE };
    }
  
    return { valid: true, error: null };
  });

  // ===========================================================================
  // Lifecycle
  // ===========================================================================

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open']?.currentValue === true) {
      this.newGroupName.set('');
      this.hasInteracted.set(false);
    }
  }

  // ===========================================================================
  // Handlers
  // ===========================================================================

  /** Handle input changes - update value and clear errors */
  onGroupNameInputChange(value: string): void {
    this.newGroupName.set(value);
    this.hasInteracted.set(true);
    this.addGroupInputChange.emit();
  }

  onSave(): void {
    const name = this.newGroupName().trim();
    this.hasInteracted.set(true);

    // Skip if already adding
    if (this.addingGroup()) {
      return;
    }

    // Client-side validation
    const validation = this.localValidationResult();
    if (!validation.valid) {
      return;
    }

    // Emit to parent for API call
    this.addGroup.emit(name);
  }

  onCancel(): void {
    this.closePanel.emit();
  }
}